import { Injectable } from '@angular/core';
import { AbstractRequest } from '@shared/requests/AbstractRequest';
import User from '@shared/models/User';
import UpdatePassword from '@shared/models/UpdatePassword';
import CredentialToken from '@auth/models/CredentialToken';
import { HttpParams } from '@angular/common/http';
import Count from '@shared/models/Count';
import { firstValueFrom, Observable } from 'rxjs';
import Bond from '@shared/models/Bond';
import CredentialNewAdmin from '../../admin/models/CredentialNewAdmin';
import UserEditInterface from '../../owner/dtos/user-edit-dto';

@Injectable({
  providedIn: 'root',
})
export class UserService extends AbstractRequest {
  public fetchAll(
    page?: number,
    items?: number,
    status: string | null = null,
    sortBy?: string | null,
    sortDirection?: string | null,
    search?: string | null,
  ): Observable<User[]> {
    const names = ['page', 'items', 'status', 'sortBy', 'sortDirection', 'search'];
    const args = this.formatArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<User[]>(`${this.endpoint}/users`, {
        params,
      });
  }

  public fetchBonds(
    page?: number,
    items?: number,
    state: Array<string> | null = null,
    type?: string | null,
    sortBy?: string | null,
    sortDirection?: string | null,
    search?: string | null,
    startDate?: string | null,
    endDate?: string | null,
    isOwner?: boolean | null,
    id?: string | number | null
  ): Observable<Bond[]> {
    const names = ['page', 'items', 'state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate', 'isOwner'];
    const args = this.formatForStateArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Bond[]>(`${this.endpoint}/users/admin/${id}/bonds`, {
        params,
      });
  }

  public countBonds(
    state: Array<string> | null = null,
    type?: string | null,
    sortBy?: string,
    sortDirection?: string,
    search?: string,
    startDate?: string,
    endDate?: string,
    owner?: boolean | null,
    id?: string | number | null
  ): Observable<Count> {
    const names = ['state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate', 'isOwner'];
    const args = this.formatForStateArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Count>(`${this.endpoint}/users/admin/${id}/bonds/count`, {
        params,
      });
  }

  public count(status: string | null = null, sortBy?: string, sortDirection?: string, search?: string): Observable<Count> {
    const names = ['status', 'sortBy', 'sortDirection', 'search'];
    const args = this.formatArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Count>(`${this.endpoint}/users/count`, {
        params,
      });
  }

  public changeSendMailByTyllt(bool: boolean): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/changesendmail/${bool}`, {});
  }

  public updateMe(user: User|UserEditInterface): Promise<User> {
    return firstValueFrom(this.http.put<User>(`${this.endpoint}/users/me`, user));
  }

  public updateLang(user: User|UserEditInterface): Promise<User> {
    return firstValueFrom(this.http.put<User>(`${this.endpoint}/users/lang`, user));
  }

  /**
   * @deprecated Use updateMe() instead.
   * @param user
   */
  public updateMeDeprecated(user: User|UserEditInterface): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/me`, user);
  }

  public addPromotionCode(code: string): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/me/promo`, { promotionCode: code });
  }

  public updateGeneral(user: UserEditInterface): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/general`, user);
  }

  public updateSettings(user: User): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/settings`, user);
  }

  public updateSettingsAdmin(user: User, id: number): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/admin/settings/${id}`, user);
  }

  public deleteBankAccount(id: any): Observable<any> {
    return this.http.delete<any>(`${this.endpoint}/users/admin/bankAccount/${id}`);
  }

  public updateMyDocuments(user: User): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/me/documents`, user);
  }

  public fetch(id: string): Observable<User> {
    return this.http.get<User>(`${this.endpoint}/users/${id}`);
  }

  public update(user: User, id: number): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/${id}`, user);
  }

  public updatePassword(credential: UpdatePassword): Observable<void> {
    return this.http
      .put<void>(`${this.endpoint}/users/password`, {
        password: credential.password,
        plainPassword: {
          first: credential.first,
          second: credential.second,
        },
      });
  }

  public becomeOwner(): Observable<CredentialToken> {
    return this.http.post<CredentialToken>(`${this.endpoint}/users/become/owner`, {});
  }

  public becomeUser(): Observable<CredentialToken> {
    return this.http.post<CredentialToken>(`${this.endpoint}/users/become/user`, {});
  }

  public me(): Promise<User> {
    return firstValueFrom(this.http.get<User>(`${this.endpoint}/users/me`));
  }

  /**
   * @deprecated Use me() instead
   */
  public meDeprecated(): Observable<User> {
    return this.http.get<User>(`${this.endpoint}/users/me`);
  }

  public delete(id: number): Observable<User> {
    return this.http.delete<User>(`${this.endpoint}/users/${id}`);
  }

  public anonymize(id: number): Observable<User> {
    return this.http.delete<User>(`${this.endpoint}/users/${id}/anonymize`);
  }

  public addAdmin(user: CredentialNewAdmin): Observable<User> {
    return this.http.post<User>(`${this.endpoint}/users/admin`, user);
  }

  public updateAdmin(user: User, id: number): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/admin/${id}`, user);
  }

  public updateUserAdmin(user: UserEditInterface|User, id: number): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/admin/update/${id}`, user);
  }

  public disabled(user: User, id: number): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/admin/${id}/disabled`, user);
  }

  public updateAdminDocuments(user: User): Observable<User> {
    return this.http.put<User>(`${this.endpoint}/users/admin/documents/${user.id}`, user);
  }

  public fetchUserAdmin(id: string): Observable<User> {
    return this.http.get<User>(`${this.endpoint}/users/admin/${id}`);
  }
}
